/**
 * This supplies a wrapper around the global `fetch` function, which
 * adds some useful functionality that we want when making requests.
 *
 * In particular, it:
 *  - sends cookies by default on same-origin requests
 *  - fails the promise chain when we receive a non-2xx response, for backwards
 *    compatibility with $.ajax:
 *    https://github.com/github/fetch/blob/master/README.md#handling-http-error-statuses
 *
 * It also adds the same functionality that is added to `$.ajax` calls in
 * api-action-results.js, namely:
 *  - Adds a lang= parameter to API calls
 *  - Adds a cache-busting _= parameter to API calls
 *  - Adds the XSRF header
 *  - Checks for API version mismatches
 *  - Checks for API action results
 */
import {addApiParamsAndHeaders} from "./add-api-params-and-headers.ts";
import {getRequireKAALCookie} from "./require-kaal-cookie.ts";
import {sendCookies} from "./send-cookies.ts";
import {throwOnBadStatus} from "./throw-on-bad-status.ts";
import type {KhanFetchFn} from "./types.ts";

export const internalFetch: KhanFetchFn = (input, init) =>
    Promise.resolve(new Request(input, init))
        .then(sendCookies)
        .then(addApiParamsAndHeaders)
        .then(getRequireKAALCookie())

        // NOTE: this is the only place we're allowed to use the
        // native/polyfilled fetch function.
        .then((request) =>
            fetch(request).then((response) => [request, response]),
        )

        .then(([request, response]: (Request | Response)[]) =>
            throwOnBadStatus(request as Request, response as Response),
        );
